import React, { useMemo } from 'react';
import { useSubscriptionDetails } from '@features/user';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Button, Divider, Link, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { unixTimestampToDaysRemaining } from '@utils/utils';

import { useOnboardingWelcomeConfig } from '../hooks';

interface OnboardingWelcomeStepProps {
  onCompleteOnboarding: (destination: string) => void;
}

/**
 * Third step in the onboarding flow that displays:
 * - Welcome message with trial information
 * - Two CTAs (primary button + secondary link)
 * - List of features available during trial
 *
 * All content is dynamic config-driven via Statsig.
 */
export const OnboardingWelcomeStep: React.FC<OnboardingWelcomeStepProps> = ({
  onCompleteOnboarding,
}) => {
  const config = useOnboardingWelcomeConfig();
  const { data: subscriptionDetails } = useSubscriptionDetails();
  const { onboarding: onboardingMetrics } = useEventTracking();

  // Calculate trial info for placeholder replacement
  const trialInfo = useMemo(() => {
    const trialEnd = subscriptionDetails?.trial_end || subscriptionDetails?.current_period_end;

    if (!trialEnd) {
      // Default to 10 days from today if no trial info available
      const defaultDaysRemaining = 10;
      const defaultEndDate = new Date();
      defaultEndDate.setDate(defaultEndDate.getDate() + defaultDaysRemaining);
      const trialEndDate = defaultEndDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      return { daysRemaining: String(defaultDaysRemaining), trialEndDate };
    }

    const daysRemaining = unixTimestampToDaysRemaining(trialEnd);
    const trialEndDate = new Date(trialEnd * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

    return {
      daysRemaining: String(daysRemaining),
      trialEndDate,
    };
  }, [subscriptionDetails?.trial_end, subscriptionDetails?.current_period_end]);

  // Replace placeholders in subtitle
  const formattedSubtitle = useMemo(() => {
    return config.subtitle
      .replace('{daysRemaining}', trialInfo.daysRemaining)
      .replace('{trialEndDate}', trialInfo.trialEndDate);
  }, [config.subtitle, trialInfo]);

  const handlePrimaryCtaClick = () => {
    onboardingMetrics.pathSelected(config.primaryCta.trackingId);
    onCompleteOnboarding(config.primaryCta.destination);
  };

  const handleSecondaryCtaClick = () => {
    onboardingMetrics.pathSelected(config.secondaryCta.trackingId);
    onCompleteOnboarding(config.secondaryCta.destination);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: 4,
        px: 2,
      }}
    >
      {/* Title */}
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 600,
          mb: 2,
          fontSize: { xs: '1.75rem', sm: '2.125rem' },
        }}
      >
        {config.title}
      </Typography>

      {/* Subtitle with trial info */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          mb: 1,
          color: 'text.primary',
          fontSize: { xs: '1rem', sm: '1.125rem' },
        }}
      >
        {formattedSubtitle}
      </Typography>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          mb: 4,
        }}
      >
        {config.description}
      </Typography>

      {/* Primary CTA Button */}
      <Button
        variant="contained"
        size="large"
        color="primary"
        onClick={handlePrimaryCtaClick}
        sx={{
          mb: 2,
          px: 6,
          py: 1.5,
          fontSize: '1rem',
          textTransform: 'none',
        }}
      >
        {config.primaryCta.label}
      </Button>

      {/* Secondary CTA Link */}
      <Link
        component="button"
        variant="body2"
        onClick={handleSecondaryCtaClick}
        sx={{
          color: 'text.secondary',
          textDecoration: 'underline',
          cursor: 'pointer',
          mb: 4,
          '&:hover': {
            color: 'text.primary',
          },
        }}
      >
        {config.secondaryCta.label}
      </Link>

      {/* Divider */}
      <Divider sx={{ width: '100%', maxWidth: '500px', my: 3 }} />

      {/* Feature List */}
      <Box sx={{ width: '100%', maxWidth: '500px', textAlign: 'left' }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 2,
          }}
        >
          {config.featureList.title}
        </Typography>

        <List dense disablePadding>
          {config.featureList.items.map((item, index) => (
            <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.25rem' }} />
              </ListItemIcon>
              <ListItemText
                primary={item}
               slotProps={{ primary: {
                  variant: 'body2',
                  color: 'text.primary',
                } }}/>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};
